import { useState, useEffect, useCallback } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './supabase'
import Sidebar from './components/Sidebar'
import Toast from './components/Toast'
import Login from './pages/Login'
import Inventario from './pages/Inventario'
import Horarios from './pages/Horarios'
import Citas from './pages/Citas'
import PostVenta from './pages/PostVenta'
import Configuracion from './pages/Configuracion'
import ConectarWhatsapp from './pages/ConectarWhatsapp' 
import Suscripcion from './pages/Suscripcion' 

function AppContent({ session }) {
  const [comercioId, setComercioId] = useState(null)
  const [suscripcion, setSuscripcion] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [toast, setToast] = useState({ message: '', type: 'success' })
  
  // 🛡️ Agregamos un estado de carga para bloquear la UI hasta verificar la DB
  const [cargandoComercio, setCargandoComercio] = useState(true)

  // 🔒 Movemos cerrarSesion dentro de un useCallback para poder usarlo en fetchComercioId
  const cerrarSesion = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  const fetchComercioId = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from('comercios')
        // 🚀 AÑADIMOS creditos_demo a la consulta de Supabase
        .select('id, mensajes_disponibles, creditos_demo, plan_actual, estado_suscripcion, plan_vence_el')
        .eq('owner_id', userId)
        .maybeSingle()

      if (error) throw error
      
      if (data) {
        setComercioId(data.id)
        setSuscripcion({
          estado_suscripcion: data.estado_suscripcion || 'trial',
          mensajes_disponibles: data.mensajes_disponibles ?? 0,
          // 🚀 GUARDAMOS creditos_demo en el estado (por defecto 50 si es null)
          creditos_demo: data.creditos_demo ?? 50,
          plan_actual: data.plan_actual || 'trial',
          plan_vence_el: data.plan_vence_el || null
        })
      } else {
        // 🚨 SESIÓN FANTASMA DETECTADA: Tiene token, pero no existe en la tabla comercios.
        // Lo expulsamos automáticamente para limpiar su navegador.
        await cerrarSesion()
      }
    } catch (err) {
      console.error("Error cargando comercio:", err)
    } finally {
      // ✅ Ya verificamos la base de datos, quitamos la pantalla de carga
      setCargandoComercio(false)
    }
  }, [cerrarSesion])

  useEffect(() => {
    if (session?.user?.id) {
      fetchComercioId(session.user.id)
    }
  }, [session, fetchComercioId])

  const showToast = (message, type = 'success') => setToast({ message, type })
  const hideToast = () => setToast({ message: '', type: 'success' })

  // 🚧 Pantalla de bloqueo: Evita que el inventario se renderice con comercioId en null
  if (cargandoComercio) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f0' }}>
        <p style={{ color: '#888', fontSize: 14 }}>Verificando cuenta...</p>
      </div>
    )
  }

  // Si después de cargar, seguimos sin comercioId (porque lo expulsó), no renderizamos las rutas
  if (!comercioId) return null

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f0' }}>
      
      <Sidebar 
        open={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        onLogout={cerrarSesion} 
        suscripcion={suscripcion} 
      />
      
      <header className="mobile-header" style={{ background: '#fff', borderBottom: '0.5px solid #e0e0d8', padding: '1rem', position: 'sticky', top: 0, zIndex: 50, display: 'none', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: 'none', fontSize: 22, color: '#1a1a1a' }}>☰</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>iStore Admin</span>
          {suscripcion?.estado_suscripcion === 'activa' && (
            <span style={{ background: '#10b981', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
              PRO
            </span>
          )}
        </div>
        <button onClick={cerrarSesion} style={{ background: 'none', border: 'none', fontSize: 13, color: '#888' }}>Salir</button>
      </header>

      <main style={{ marginLeft: 220, padding: '2rem' }} className="main-content">
        <Routes>
          <Route path="/" element={<Inventario toast={showToast} comercioId={comercioId} />} />
          <Route path="/horarios" element={<Horarios toast={showToast} comercioId={comercioId} />} />
          <Route path="/citas" element={<Citas toast={showToast} comercioId={comercioId} />} />
          <Route path="/postventa" element={<PostVenta toast={showToast} comercioId={comercioId} />} />
          <Route path="/configuracion" element={<Configuracion toast={showToast} comercioId={comercioId} />} />
          <Route path="/suscripcion" element={<Suscripcion comercioId={comercioId} toast={showToast} />} />
          <Route path="/whatsapp" element={<ConectarWhatsapp comercioId={comercioId} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>

      <Toast message={toast.message} type={toast.type} onClose={hideToast} />
    </div>
  )
}

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMounted) {
        setSession(session)
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isMounted) {
        setSession(session)
        if (!session) setLoading(false)
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [])

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f0' }}>
      <p style={{ color: '#888', fontSize: 14 }}>Cargando panel...</p>
    </div>
  )

  if (!session) return <Login />

  return (
    <BrowserRouter>
      <AppContent session={session} />
      
      <style>{`
        .mobile-header { display: none; }
        @media (max-width: 768px) {
          .mobile-header { display: flex !important; }
          .main-content { margin-left: 0 !important; padding: 1rem !important; }
        }
      `}</style>
    </BrowserRouter>
  )
}